import {useQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {Divider, Stack, Button} from "@mui/material";
import Grid from '@mui/material/Grid';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {Outlet, useLocation, useNavigate} from "react-router-dom";

import {GET_ALL_CONTENT_DATA} from "../queries/GraphQLQueries";
import OpenFile from "../components/OpenFile";
import Loader from "../components/Loader";
import {toTitleCase} from "../helpers/TextUtils";
import ContentHierarchyTree from "../components/content-hierarchy/ContentHierarchyTree";
import ContentDetailsCard from "../components/content-hierarchy/ContentDetailsCard";
import {NODE_LEVEL} from "../domain/HierarchyConstants";
import {CONTENT_OFFSET} from "../domain/ContentOffset";
import {getContentCapLevelName} from "../helpers/CapLevelHelper";

export default function ContentHierarchy() {

    const navigate = useNavigate();
    const { state } = useLocation();

    const {data: updatedContent} = state || {};
    const {loading, data} = useQuery(GET_ALL_CONTENT_DATA);
    const [contentData, setContentData] = useState([]);
    const [selectedNode, setSelectedNode] = useState({});
    const [selectedContentDetails, setSelectedContentDetails] = useState();
    const [enableNewContentAction, setEnableNewContentAction] = useState(false);
    const [enableAllContentActions, setEnableAllContentActions] = useState(false);

    useEffect(() => {

        if (!data?.serviceTypes || loading) {
            return;
        }

        const serviceTypes = data.serviceTypes.map((item) => {
            return {
                id: item.id,
                originalId: item.id,
                serviceTypeId: item.id,
                text: item.description,
                serviceTypeKeyCode: item.keycode,
                droppable: true,
                parent: 0,
                type: NODE_LEVEL.ROOT,
                color: item.primaryColor
            }
        });

        const contentFolders = data.contentFolders.map((item) => {
            return {
                id: item.id + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET),
                originalId: item.id,
                serviceTypeId: item.serviceType.id,
                serviceTypeKeyCode: item.serviceType.keycode,
                text: item.name,
                droppable: true,
                parent: item.parentFolderId ? item.parentFolderId + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET)
                  : item.serviceType.id,
                originalParentId: item.parentFolderId ? item.parentFolderId : item.serviceType.id,
                type: NODE_LEVEL.BRANCH
            }
        });

        const contentInfos = data.contentInfos.map((item) => {
            return {
                id: item.id + (item.serviceType.id * CONTENT_OFFSET.CONTENT_INFO_ID_OFFSET),
                originalId: item.id,
                serviceTypeId: item.serviceType.id,
                serviceTypeKeyCode: item.serviceType.keycode,
                text: item.name,
                parent: item.contentFolderId ?
                  item.contentFolderId + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET)
                  : item.serviceType.id,
                serviceType: item.serviceType.description,
                description: item.description,
                externalName: item.nameExternal,
                code: item.code,
                created: item.created,
                contentType: toTitleCase(item.contentType),
                capLevel: item.level,
                isAssessed: item.isAssessed,
                content: renderContentFileUrl(item.externalUrl, item.externalUrlName),
                externalUrl: item.externalUrl,
                externalUrlName: item.externalUrlName,
                originalContentType: item.contentType,
                type: NODE_LEVEL.CHILD
            }
        });

        setContentData([...serviceTypes, ...contentFolders, ...contentInfos]);
    }, [data]);

    useEffect(() => {
        if (selectedContentDetails && updatedContent?.data?.updateContentInfo) {
            const updateContentInfo = updatedContent.data.updateContentInfo;
            setSelectedContentDetails({
                ...selectedContentDetails,
                text: updateContentInfo.name,
                externalName: updateContentInfo.nameExternal,
                code: updateContentInfo.code,
                capLevel: updateContentInfo.level,
                contentType: updateContentInfo.contentType,
                isAssessed: updateContentInfo.isAssessed,
                description: updateContentInfo.description,
                externalUrl: updateContentInfo.externalUrl,
                content: {
                    ...selectedContentDetails.content,
                    props: {
                        ...selectedContentDetails.content.props,
                        fileName: updateContentInfo.externalUrlName,
                        fileUrl: updateContentInfo.externalUrl
                    }
                }
            })
        }
    }, [updatedContent]);

    const renderContentFileUrl = (externalUrl, externalUrlName) => <OpenFile fileName={externalUrlName} fileUrl={externalUrl} />;

    const handleCreateContent = () => {
        const contentFolderId = selectedContentDetails?.type !== NODE_LEVEL.ROOT ? selectedContentDetails?.originalId : null;
        navigate('new', {
            state: {
                contentFolderId,
                currentServiceTypeKeycode: selectedNode.serviceTypeKeyCode,
                isEditDialogBox: false,
            }
        });
    }

    const handleEditContent = () => {

        if (!selectedContentDetails) {
            return;
        }
        navigate('edit', {
            state: {
                contentId: selectedContentDetails.originalId,
                contentDescription: selectedContentDetails.description,
                contentName: selectedContentDetails.text,
                contentNameExternal: selectedContentDetails.externalName,
                contentCode: selectedContentDetails.code,
                contentExternalUrl: selectedContentDetails.externalUrl,
                contentExternalUrlName: selectedContentDetails.externalUrlName,
                currentServiceTypeKeycode: selectedContentDetails.serviceTypeKeyCode,
                currentServiceType: selectedContentDetails.serviceType,
                contentLevel: getContentCapLevelName(selectedContentDetails.capLevel),
                contentIsAssessed: selectedContentDetails.isAssessed,
                currentContentType: selectedContentDetails.originalContentType,
                isEditDialogBox: true
            }
        });
    }

    const handleDeleteContent = () => {
        if (!selectedContentDetails) {
            return;
        }
        navigate('delete', {
            state: {
                contentId: selectedContentDetails.originalId,
                contentName: selectedContentDetails.text
            }
        });
        setSelectedContentDetails(null);
        setEnableAllContentActions(false);
    }

    if (loading) {
        return <Loader/>;
    }

    return (
      <Stack>
          <Grid container>
              <Grid size={6} style={{overflow: "auto"}}>
                  <ContentHierarchyTree
                    contentData={contentData}
                    selectedNode={selectedNode}
                    setSelectedNode={setSelectedNode}
                    setSelectedContentDetails={setSelectedContentDetails}
                    setEnableNewContentAction={setEnableNewContentAction}
                    setEnableAllContentActions={setEnableAllContentActions} />
              </Grid>
              <Divider orientation="vertical" flexItem/>
              <Grid size={5.5} pl={2}>
                  <Button size="small" sx={{m: 0.5}} variant="contained" disabled={!enableNewContentAction} startIcon={<AddCircleIcon />} onClick={() => handleCreateContent()}>New</Button>
                  <Button size="small" sx={{m: 0.5}} variant="contained" disabled={!enableAllContentActions} startIcon={<EditIcon />} onClick={() => handleEditContent()}>Edit</Button>
                  <Button size="small" sx={{m: 0.5}} variant="contained" disabled={!enableAllContentActions} startIcon={<DeleteIcon />} onClick={() => handleDeleteContent()}>Delete</Button>
                  {selectedContentDetails
                    && !selectedContentDetails?.droppable
                    && <ContentDetailsCard contentDetails={selectedContentDetails} />}
              </Grid>
          </Grid>
          <Outlet/>
      </Stack>
    );
}